import type { JobRecommendation } from '../../react-app/routes/discover';
import { Badge } from '../ui';
import { Loader2, Check, X } from 'lucide-react';

export function RecommendationCard({
  job,
  selected,
  onClick,
  onSave,
  onApply,
  onReject,
  onFeedback,
  optimisticAction,
}: {
  job: JobRecommendation;
  selected?: boolean;
  onClick?: () => void;
  onSave: (job: JobRecommendation) => void;
  onApply: (job: JobRecommendation) => void;
  onReject: (job: JobRecommendation) => void;
  onFeedback: (job: JobRecommendation, signal: string) => void;
  optimisticAction?: 'saving' | 'saved' | 'applying' | 'applied' | 'rejecting' | 'rejected';
}) {
  const getStrategyColor = (strategy: string): 'neutral' | 'info' | 'positive' | 'secondary' | 'primary' | 'caution' | 'success' => {
    switch (strategy) {
      case 'HIGH_COMP': return 'info';
      case 'RESUME_TAGS': return 'primary';
      case 'EASY_WIN': return 'positive';
      case 'SMALL_CO': return 'caution';
      case 'REMOTE': return 'neutral';
      default: return 'neutral';
    }
  };

  const formatStrategy = (strategy: string) => {
    switch (strategy) {
      case 'HIGH_COMP': return 'High Comp';
      case 'RESUME_TAGS': return 'Resume Tags';
      case 'EASY_WIN': return 'Easy Win';
      case 'SMALL_CO': return 'Small Co';
      case 'REMOTE': return 'Remote';
      default: return strategy;
    }
  };

  return (
    <div
      role="button"
      onClick={onClick}
      className={`rounded-2xl border bg-card p-4 shadow-card hover:border-border-hover hover:shadow-card-hover focus-within:ring-2 focus-within:ring-primary/40 active:bg-muted/50 transition-all cursor-pointer ${
        selected ? 'border-primary' : 'border-border'
      }`}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="flex-1">
          <div className="font-medium text-foreground">{job.title}</div>
          <div className="text-sm text-muted-foreground">
            {job.company} • {job.location}
            {job.salary && ` • ${job.salary}`}
          </div>
          
          {/* Strategy and confidence badges */}
          <div className="mt-2 flex flex-wrap gap-2">
            <Badge variant={getStrategyColor(job.strategy)} className="text-xs">
              Strategy: {formatStrategy(job.strategy)}
            </Badge>
            {typeof job.confidence === 'number' && (
              <Badge variant="neutral" className="text-xs">
                Confidence {Math.round(job.confidence * 100)}%
              </Badge>
            )}
            {job.source && (
              <Badge variant="neutral" className="text-xs">
                {job.source}
              </Badge>
            )}
          </div>
          
          {/* Rationale */}
          <p className="mt-2 text-sm text-muted-foreground">
            <span className="font-medium">Why:</span> {job.rationale}
          </p>
          
          {/* Tags */}
          {job.tags && job.tags.length > 0 && (
            <div className="mt-2 flex flex-wrap gap-2 text-xs">
              {job.tags.map((tag) => (
                <span 
                  key={tag} 
                  className="rounded-full border border-border px-2.5 py-0.5 text-muted-foreground bg-muted/30"
                >
                  {tag}
                </span>
              ))}
            </div>
          )}
        </div>
        
        {/* Action buttons */}
        <div className="shrink-0 flex gap-2">
          <button 
            onClick={(e) => { e.stopPropagation(); onReject(job); }} 
            disabled={optimisticAction === 'rejecting' || optimisticAction === 'rejected'}
            className={`rounded-xl border px-3 py-1.5 hover:bg-muted focus:outline-none focus:ring-2 focus:ring-primary/40 transition-all text-sm flex items-center gap-1 ${
              optimisticAction === 'rejecting' || optimisticAction === 'rejected'
                ? 'border-destructive bg-destructive/10 text-destructive cursor-not-allowed'
                : 'border-border'
            }`}
          >
            {optimisticAction === 'rejecting' && <Loader2 className="h-3 w-3 animate-spin" />}
            {optimisticAction === 'rejected' && <X className="h-3 w-3" />}
            {optimisticAction === 'rejected' ? 'Rejected' : 'Reject'}
          </button>
          <button 
            onClick={(e) => { e.stopPropagation(); onSave(job); }} 
            disabled={optimisticAction === 'saving' || optimisticAction === 'saved'}
            className={`rounded-xl border px-3 py-1.5 hover:bg-muted focus:outline-none focus:ring-2 focus:ring-primary/40 transition-all text-sm flex items-center gap-1 ${
              optimisticAction === 'saving' || optimisticAction === 'saved'
                ? 'border-positive bg-positive/10 text-positive cursor-not-allowed'
                : 'border-border'
            }`}
          >
            {optimisticAction === 'saving' && <Loader2 className="h-3 w-3 animate-spin" />}
            {optimisticAction === 'saved' && <Check className="h-3 w-3" />}
            {optimisticAction === 'saved' ? 'Saved' : optimisticAction === 'saving' ? 'Saving...' : 'Save'}
          </button>
          <button 
            onClick={(e) => { e.stopPropagation(); onApply(job); }} 
            disabled={optimisticAction === 'applying' || optimisticAction === 'applied'}
            className={`rounded-xl px-3 py-1.5 hover:opacity-95 focus:outline-none focus:ring-2 focus:ring-primary/40 transition-all text-sm flex items-center gap-1 ${
              optimisticAction === 'applying' || optimisticAction === 'applied'
                ? 'bg-positive text-positive-foreground cursor-not-allowed'
                : 'bg-primary text-primary-foreground'
            }`}
          >
            {optimisticAction === 'applying' && <Loader2 className="h-3 w-3 animate-spin" />}
            {optimisticAction === 'applied' && <Check className="h-3 w-3" />}
            {optimisticAction === 'applied' ? 'Applied' : optimisticAction === 'applying' ? 'Applying...' : 'Apply'}
          </button>
        </div>
      </div>
      
      {/* Feedback signals */}
      <div className="mt-3 flex flex-wrap gap-2 text-xs">
        {['too_senior', 'wrong_stack', 'low_pay', 'too_far', 'not_industry'].map((signal) => (
          <button
            key={signal}
            onClick={(e) => { 
              e.stopPropagation(); 
              onFeedback(job, signal); 
            }}
            className="rounded-xl border border-border px-2.5 py-1 hover:bg-muted focus:outline-none focus:ring-2 focus:ring-primary/40 transition-all text-muted-foreground"
          >
            {formatFeedbackLabel(signal)}
          </button>
        ))}
      </div>
    </div>
  );
}

function formatFeedbackLabel(key: string): string {
  switch (key) {
    case 'too_senior': return 'Too senior';
    case 'wrong_stack': return 'Wrong stack';
    case 'low_pay': return 'Low pay';
    case 'too_far': return 'Too far';
    case 'not_industry': return 'Not my industry';
    default: return key;
  }
}